import { useRef, useState } from 'react';
import { MdMoreVert, MdDescription } from 'react-icons/md';
import { Button } from '@/components/Button';
import { renderTextWithLinks } from '@/utils/textWithLinks';
import useOnClickOutside from '../../job_change_processes/hooks/useOnClickOutside';
import type { AgentApiCompanyChatCommentsRecord } from '@/types/serializers';

type Props = {
  chatComment: AgentApiCompanyChatCommentsRecord;
  canDelete: boolean;
  onDelete: (chatCommentId: string) => void;
};

export default function CompaniesTimelineItem({ chatComment, canDelete, onDelete }: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(menuRef, () => setIsMenuOpen(false));

  const avatarUrl =
    chatComment.source_type === 'user'
      ? chatComment.author?.avatar_url || '/agent/default-avatar.png'
      : '/agent/candidates/icon-chat-system.svg';

  return (
    <div className="flex items-start gap-sm rounded-sm bg-white p-md shadow-layer1">
      <img
        src={avatarUrl}
        width={42}
        height={42}
        alt=""
        className="size-[42px] shrink-0 rounded-full object-cover"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-xs">
        <div className="flex items-center justify-between gap-xs">
          {chatComment.source_type === 'user' && (
            <p className="truncate text-heading-xxs">{chatComment.author?.name}</p>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-xs">
            <p className="text-body-xs text-placeholder">{chatComment.posted_at}</p>
            {canDelete && (
              <div className="relative" ref={menuRef}>
                <Button
                  type="button"
                  color="secondary"
                  size="sm"
                  iconOnly
                  active={isMenuOpen}
                  aria-label="メニュー"
                  onClick={() => setIsMenuOpen((prev) => !prev)}
                >
                  <MdMoreVert className="size-4" />
                </Button>
                {isMenuOpen && (
                  <div className="absolute top-9 right-0 z-10 overflow-hidden rounded-sm bg-white shadow-layer2">
                    <button
                      type="button"
                      className="flex h-xxl w-full cursor-pointer items-center border-0 bg-white px-sm py-xs text-left text-body-sm whitespace-nowrap text-danger hover:bg-gray-50"
                      onClick={() => {
                        setIsMenuOpen(false);
                        onDelete(chatComment.id);
                      }}
                    >
                      削除
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-xxs">
          {chatComment.attachments.length > 0 && (
            <div className="flex flex-wrap gap-xxs">
              {chatComment.attachments.map((attachment) => (
                <a
                  key={attachment.id}
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={attachment.filename}
                  className="flex h-xl max-w-60 items-center gap-xxs rounded-sm border border-solid border-border bg-white px-xs text-body-xs text-main no-underline hover:bg-gray-50"
                >
                  <MdDescription className="size-4 shrink-0" />
                  <span className="min-w-0 truncate">{attachment.filename}</span>
                </a>
              ))}
            </div>
          )}
          <p className="text-body-xs whitespace-pre-wrap text-main">
            {chatComment.content.map((c, index) => (
              <span key={index}>{renderTextWithLinks(c.text)}</span>
            ))}
          </p>
        </div>
      </div>
    </div>
  );
}
